import { Lang } from '@axl/membership-sdk';
import '../shared/lib/extensions';
import { App as AntApp, ConfigProvider, message, theme as antThemeApi } from 'antd';
import enUS from 'antd/es/locale/en_US';
import esES from 'antd/es/locale/es_ES';
import idID from 'antd/es/locale/id_ID';
import ruRU from 'antd/es/locale/ru_RU';
import ukUA from 'antd/es/locale/uk_UA';
import zhCN from 'antd/es/locale/zh_CN';
import zhTW from 'antd/es/locale/zh_TW';
import ltLT from 'antd/es/locale/lt_LT';
import dayjs from 'dayjs';
import moment from 'moment';
// #region i18n
import 'moment/dist/locale/es';
import 'moment/dist/locale/id';
import 'moment/dist/locale/ru';
import 'moment/dist/locale/uk';
import 'moment/dist/locale/zh-cn';
import 'moment/dist/locale/zh-tw';
import 'moment/dist/locale/lt';
// #endregion
import { FC, useEffect, useLayoutEffect, useMemo } from 'react';

import { Pages } from '@app/pages/index';
import { AppProvider } from '@app/shared/providers/AppProvider';
import { ChatProvider } from '@app/features/chat/ChatProvider';
import { DeviceProvider } from '@app/shared/providers/DeviceProvider';
import { GamificationBalanceProvider } from '@app/shared/providers/GamificationBalanceProvider';
import { useLang, useSchool } from '@app/shared/lib/hooks';
import { buildAntThemeConfig, CSS_VAR_KEY } from '@app/shared/lib/theme/antTheme';
import { accentFor, applyDesignTokens, designAntLook } from '@app/shared/lib/design/apply-design';
import { usePortalDesign } from '@app/shared/lib/design/portal-design';
import { applyAccentTokens, liftedDarkSeed } from '@app/shared/lib/theme/applyAccentTokens';
import { setStaticPrimary } from '@app/shared/lib/theme/staticAntTheme';
import { ThemeProvider, useTheme } from '@app/shared/providers/ThemeProvider';
import { LocProvider } from '@app/shared/lib/localization';
import { SchoolProvider } from '@app/shared/providers/SchoolProvider';
import { SdkProvider } from '@app/shared/providers/SdkProvider';
import '@app/shared/ui/styles/global.scss';
import { UIAppStateProvider } from '@app/shared/ui/UIAppState/UIAppStateProvider';

import StagingBadge from './staging/StagingBadge';

import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/ru';

dayjs.extend(updateLocale);

dayjs.updateLocale('ru', {
    weekStart: 1
});
const AppConfig: React.FC = () => {
    const { lang } = useLang();
    const school = useSchool();
    const { resolved, setPolicy } = useTheme();
    const design = usePortalDesign();
    // the accent of the theme on screen (portal design brand.darkPrimaryColor in the dark one)
    const primary = accentFor(design, school.primaryColor, resolved);
    const look = useMemo(() => designAntLook(design), [design]);

    const liftDark = resolved === "dark" && !design.brand.darkPrimaryColor;
    const antTheme = useMemo(() => {
        const config = buildAntThemeConfig(resolved, primary, CSS_VAR_KEY, look);
        if (!liftDark) return config;
        // dark theme, derived accent: links (school name, event titles) take the lifted accent as their seed, so antd's
        // dark ramp of it matches the text accent; buttons stay as they are
        const seed = liftedDarkSeed(primary, antThemeApi.getDesignToken(config).colorPrimary);
        return { ...config, token: { ...config.token, colorLink: seed } };
    }, [resolved, primary, look, liftDark]);

    useEffect(() => {
        // no own dark accent: the derived one is lifted for text and icons to stay readable
        applyAccentTokens(resolved, primary, look.buttonColor, !design.brand.darkPrimaryColor);
        setStaticPrimary(primary, look);
    }, [resolved, primary, look, design.brand.darkPrimaryColor]);

    // portal design: the school's theme policy, fonts / type scale / radii / reading width as tokens
    useEffect(() => setPolicy({ default: design.theme.default, allowSwitch: design.theme.allowSwitch }), [design.theme.default, design.theme.allowSwitch, setPolicy]);
    useLayoutEffect(() => applyDesignTokens(design), [design]);

    useEffect(() => {
        message.config({ top: 70 });
    }, []);

    useMemo(() => {
        switch (lang) {
            case Lang.zhtw:
                moment.locale('zh-tw');
                break;
            case Lang.zhcn:
                moment.locale('zh-cn');
                break;
            default:
                moment.locale(lang);
                break;
        }
    }, [lang]);

    const antLocale = useMemo(() => {
        switch (lang) {
            case Lang.en:
                return enUS;
            case Lang.id:
                return idID;
            case Lang.ru:
                return ruRU;
            case Lang.uk:
                return ukUA;
            case Lang.es:
                return esES;
            case Lang.zhtw:
                return zhTW;
            case Lang.zhcn:
                return zhCN;
            case Lang.lt:
                return ltLT;
            default:
                break;
        }
    }, [lang]);

    return (
        <ConfigProvider locale={antLocale} theme={antTheme}>
            <AntApp>
                <Pages />
                <StagingBadge />
            </AntApp>
        </ConfigProvider>
    );
};

export const App: FC = () => (
    <SdkProvider>
        <ThemeProvider>
        <DeviceProvider>
            <ChatProvider>
                <AppProvider>
                    <SchoolProvider>
                        <LocProvider>
                            <UIAppStateProvider>
                                <GamificationBalanceProvider>
                                    <AppConfig />
                                </GamificationBalanceProvider>
                            </UIAppStateProvider>
                        </LocProvider>
                    </SchoolProvider>
                </AppProvider>
            </ChatProvider>
        </DeviceProvider>
        </ThemeProvider>
    </SdkProvider>
);
